import { PRODUCT, SERVICE } from '@constant/types';
import { showError } from '@context/actions';
import { syncLocalStorageAppointment } from '@context/actions/appointment';
import { SharedLinkDataProvider } from '@hook/sharedLinkData';
import Default from '@layout/Default';
import HeadMeata from "@module/headMetaTags";
import PdpPage from "@module/pdp/pdp";
import RedeemablePurchaseView from '@module/reedemablesListView/redeemablePurchaseView';
import ServicePdpModal from '@module/servicePdpModal';
import { loadSharedLinkPayload, SharedLinkPayload } from '@storeData/sharedLink';
import { resolveSharedLinkRequest } from '@util/sharedLinkRoutes';
import { getPromotionByCode, getStoreConfigs, getTenantDataByTenantId } from '@storeData/store';
import Home from '@template/Home';
import AdvanceBalanceInvoicePage from '@template/advBalInvoice';
import Appointment from '@template/appointment';
import BookedSlotAppointmentFlow from '@template/appointment/bookedSlotAppointmentFlow';
import CartPage from '@template/cartPage';
import AllCategoryPage from '@template/categories/allCategories';
import CategoryPage from '@template/categories/categories';
import CheckoutPage from '@template/checkout';
import CustomForms from '@template/customForms';
import FeedbackPage from '@template/feedback';
import GiftCardInvoicePage from '@template/giftCardInvoice';
import InvoicePage from '@template/invoice';
import MembershipInvoicePage from '@template/membershipInvoice';
import AppointmentHistoryPage from '@template/myappointments';
import OrderHistoryPage from '@template/myorders';
import OrderConfirmation from '@template/orderConfirmation';
import PackageInvoicePage from '@template/packageInvoice';
import PaymentPage from '@template/payment';
import Privacy from '@template/privacy';
import PostCarePage from '@template/postCarePage';
import ProfilePage from '@template/profile';
import ReferralPage from '@template/referral';
import AddReferralPage from '@template/referral/addReferral';
import StoreLocator from '@template/storeLocator';
import Terms from '@template/terms';
import { getMetaTagsData } from '@util/metaTagsService';
import { readLocalStorage, writeLocalStorage } from '@util/safeStorage';
import { hex2rgb, parseCookies } from "@util/utils";
import { windowRef } from "@util/window";
import { updateCurrentPage, updateErrorStatus, updateGroupStatus } from "app/redux/actions/common";
import { syncLocalStorageOrder } from "app/redux/actions/order";
import { updateStore } from "app/redux/actions/store";
import { updateUserData } from 'app/redux/actions/user';
import { wrapper } from 'app/redux/store/store';
import { useRouter } from 'next/router';
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from 'react-redux';

export const getServerSideProps = wrapper.getServerSideProps(async ({ store, req, res, query }) => {
  // Shared-link pages embed a specific customer's order in the HTML, so they must never be held by
  // a shared CDN even though the id in the URL already scopes the cache key.
  res.setHeader(
    'Cache-Control',
    resolveSharedLinkRequest(query)
      ? 'private, no-store'
      : 'public, s-maxage=10, stale-while-revalidate=59'
  )
  const host = req.headers.host;

  let metaTags: any = null;
  let storeData: any = null;
  let serverError: any = null;
  const tenantQuery: any = query.tenant;
  // Catalogue routes: /{slug}-{tenantId}/{store}/… (e.g. respark-2/kalyaninagar).
  // Old no-hyphen parsing took the last *character*; probes like /.well-known → tenantId=known.
  const tenantId = String(tenantQuery || '')
    .split('-')
    .filter(Boolean)
    .pop() || '';
  const storeQuery: any = query.store;
  const [storeName, storeId = ''] = String(storeQuery || '').split('-');

  const storeState = store.getState();

  if (query.store == "images" || query.tenant == "assets") {//when route is inactive store then query is { pagepath: ['bg.png'] ,store: "images" ,tenant: "assets"} 
    //initialise store with default values
    const tenantStoreData = { storeMetaData: {}, baseRouteUrl: '', storeData: { configData: {}, categories: [], curatedGroups: [], sliders: [] }, validPagepath: false, validStore: true };
    store.dispatch(updateStore({ ...tenantStoreData }));
    return { props: { storeData: { configData: {}, categories: [], curatedGroups: [], sliders: [] }, activeGroup: '', metaTags: {} } }
  }

  const redirectNotFound = () => ({
    redirect: {
      permanent: false,
      destination: "/404",
    },
  });

  // Require slug-{numericId}. Blocks /v1/graphql (old bug → tenantId=1), /wp-admin, etc.
  const tenantSegment = String(tenantQuery || '');
  if (!/^.+-\d+$/.test(tenantSegment) || !/^\d+$/.test(tenantId)) {
    return redirectNotFound();
  }

  // Reject literal /null, file-like segments, and empty store slugs before PCS calls.
  if (
    !storeName ||
    /^(null|undefined)$/i.test(storeName) ||
    storeName.includes('.') ||
    !/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(storeName)
  ) {
    return redirectNotFound();
  }

  const baseRouteUrl = `/${tenantQuery}/${storeQuery}/`; //current base url for routing
  const baseApiUrl = `/tenants/stores/tenantStore/name?tenantId=${tenantId}&name=${storeName}`; //current base url for routing
  let tenantData: any = null;
  try {
    tenantData = await getTenantDataByTenantId(tenantId);
  } catch (err: any) {
    console.error(
      'getTenantDataByTenantId failed',
      tenantId,
      err?.response?.status || err?.message || err,
    );
    return redirectNotFound();
  }

  if (tenantData && 'validLicence' in tenantData ? Boolean(tenantData?.validLicence) : true) {
    // get gender from client cookie start
    let groupFromCookie: any = parseCookies(req);
    if (groupFromCookie && groupFromCookie.grp) {
      groupFromCookie = groupFromCookie.grp;
    } else {
      let groups: any = tenantData.groups || 'both';
      groups = groups.split(',');
      groupFromCookie = groups[0];
    }
    // get gender from client cookie end

    //get storedata from api start
    const configBaseApiUrl = `/storeName?tenantId=${tenantId}&name=${storeName}`
    if (storeState && !storeState?.storeData && !storeState?.storeData?.storeId) {
      await getStoreConfigs(configBaseApiUrl, baseApiUrl, groupFromCookie).then(async (response: any) => {
        if (response && response.storeData) {
          if ('validLicence' in response.storeMetaData ? Boolean(response?.storeMetaData?.validLicence) : true) {
            if (response.storeData && (response?.configData?.storeConfig?.basicConfig?.catalogue ? true : (Boolean(query?.pagepath?.includes('invoice'))
              || Boolean(query?.pagepath?.includes('membershipinvoice'))
              || Boolean(query?.pagepath?.includes('packageinvoice'))
              || Boolean(query?.pagepath?.includes('giftcardinvoice'))
              || Boolean(query?.pagepath?.includes('advanceinvoice'))
              || Boolean(query?.pagepath?.includes('advancerefund'))
              || Boolean(query?.pagepath?.includes('balanceinvoice'))
              || Boolean(query?.pagepath?.includes('feedback'))
              || Boolean(query?.pagepath?.includes('form'))
              || Boolean(query?.pagepath?.includes('postcare'))))) {
              storeData = response.storeData;
              let { storeMetaData } = response;
              if (!storeMetaData) storeMetaData = {};
              storeData.description = storeMetaData.description;
              storeData.name = storeMetaData.name;
              storeData.host = host;
              storeData.url = storeMetaData.sUrl || '/';
              storeData.baseRouteUrl = baseRouteUrl;
              storeData.configData = response.configData;
              storeData.keywords = tenantData.keywords;
              if (storeData.configData && storeData.configData?.genderConfig == 'both') {
                if (!groupFromCookie) {
                  let groups: any = tenantData.groups || 'both';
                  groups = groups.split(',');
                  groupFromCookie = groups[0];
                }
                storeData.configData.genderConfig = tenantData.groups;
              }
              let availableGroups = storeData?.configData?.genderConfig || '';
              availableGroups = availableGroups.split(',');
              if (availableGroups.length == 1) {
                store.dispatch(updateGroupStatus(availableGroups[0]));
              } else {
                store.dispatch(updateGroupStatus(groupFromCookie));
              }
              const tenantStoreData = { storeMetaData, baseRouteUrl, storeData, validPagepath: false, validStore: true };
              tenantStoreData.storeData.categories = filterRetailCategories(tenantStoreData.storeData.categories);

              if (req?.url?.includes('?id')) {
                const promotionData: any = await getPromotionByCode(req.url.split('?id=')[1]);

                if (promotionData) {
                  metaTags = {
                    title: promotionData.title,
                    description: promotionData.description,
                    image: promotionData.image
                  }
                }
              } else {
                if (query.pagepath && query.pagepath.length != 0) {
                  metaTags = await getMetaTagsData(storeMetaData, storeData, query, groupFromCookie);
                  // if (!query.pagepath.includes('feedback')) {
                  // }
                } else {
                  metaTags = await getMetaTagsData(storeMetaData, storeData, query, groupFromCookie);
                }
              }
              if (metaTags && metaTags.title) {
                tenantStoreData.validPagepath = true;
              }
              store.dispatch(updateStore({ ...tenantStoreData }));
            }
          } else {
            serverError = { err: `Invalid Licence` };
            store.dispatch(updateErrorStatus(serverError))
          }
        } else {
          serverError = { err: `getStoreConfigs(${configBaseApiUrl}, ${baseApiUrl}, ${groupFromCookie}) (response && response.storeData) condition failed` };
          store.dispatch(updateErrorStatus(serverError))
        }
      }).catch((err: any) => {
        serverError = err;
        store.dispatch(updateErrorStatus(serverError))
        console.error(
          'getStoreConfigs failed',
          configBaseApiUrl,
          err?.response?.status || err?.message || err,
        );
      });

      const propsMetaTags = {
        title: metaTags?.title || '',
        description: metaTags?.description || '',
        image: metaTags?.image || '',
        siteName: metaTags?.siteName || '',
      }
      if (storeData) {
        // Full storeData already ships to the client once via the Redux initialState
        // (next-redux-wrapper). The connected page component reads it from Redux, so
        // returning it here again would double the __NEXT_DATA__ payload (~380KB extra).
        // _app-level consumers (HeadMetaTags, GtmNoScript) only need tenant/store IDs.
        const slimStoreData = { tenantId: storeData.tenantId ?? null, storeId: storeData.storeId ?? null };
        const sharedLinkPayload = await loadSharedLinkPayload(query, {
          storeId: storeData.storeId,
          tenantId: storeData.tenantId,
          req,
          storeData
        });
        return {
          props: {
            storeData: slimStoreData,
            activeGroup: groupFromCookie,
            metaTags: propsMetaTags,
            sharedLinkPayload
          },
        };
      } else {
        return {
          redirect: {
            permanent: false,
            destination: "/404"
          }
        }
      }
    }
    //get storedata from api end
  } else {
    return {
      redirect: {
        permanent: false,
        destination: "/404"
      }
    }
  }
})

const PagePath = ({
  metaTags,
  storeData: pageStoreData,
  activeGroup: pageActiveGroup,
  sharedLinkPayload
}: {
  metaTags: any;
  storeData: any;
  activeGroup: any;
  sharedLinkPayload?: SharedLinkPayload | null;
}) => {
  const reduxActiveGroup = useSelector((state: any) => state.activeGroup);
  const reduxStoreData = useSelector((state: any) => state?.store?.storeData);
  const activeGroup = reduxActiveGroup || pageActiveGroup;
  // pageStoreData is intentionally slim (tenant/store ids for HeadMetaTags only). Full catalogue
  // data arrives via next-redux-wrapper HYDRATE. Falling back to the slim props made
  // ComponentRenderer mount on multi-store client navigations before Redux was ready, and
  // Default/Home then crashed reading configData off undefined.
  const storeData = reduxStoreData?.configData ? reduxStoreData : null;

  return <SharedLinkDataProvider payload={sharedLinkPayload}>
    <HeadMeata {...metaTags} storeData={pageStoreData} />
    {Boolean(storeData) && <ComponentRenderer storeData={storeData} activeGroup={activeGroup} metaTags={metaTags} />}
  </SharedLinkDataProvider>
}

const ComponentRenderer = ({ storeData, activeGroup, metaTags }) => {
  const dispatch = useDispatch();
  const [cookie, setCookie] = useCookies();
  const [url_Segment, setUrl_Segment] = useState(null);
  const router = useRouter();
  const [currentPage, setcurrentPage] = useState(null);
  const [currentCategoryType, setCurrentCategoryType] = useState(null);
  const [enableDefaultLayout, setEnableDefaultLayout] = useState(true);//if you dont need default header navigation layout then set enableDefaultLayout to false (refer feedback page case)

  useEffect(() => {
    //detect route change
    if (storeData && storeData.storeId) {
      setEnableDefaultLayout(true);
      if (router.query.pagepath) {
        const isSubSubCat = router.asPath.includes("/sub/");
        let current_page_url: any = router.query.pagepath[0];  //single folder routing
        if (router.query.pagepath?.length && !isSubSubCat) current_page_url = (router.query.pagepath.includes('feedback')
          || router.query.pagepath.includes('payment')
          || router.query.pagepath.includes('invoice')
          || router.query.pagepath.includes('membershipinvoice')
          || router.query.pagepath.includes('packageinvoice')
          || router.query.pagepath.includes('giftcardinvoice')
          || router.query.pagepath.includes('advanceinvoice')
          || router.query.pagepath.includes('advancerefund')
          || router.query.pagepath.includes('balanceinvoice')
          || router.query.pagepath.includes('purchase')
          || router.query.pagepath.includes('postcare')
          || router.query.pagepath.includes('form')) ? router.query.pagepath[0] : router.query.pagepath[router.query.pagepath.length - 1];//if appointmentid present then page is feedback
        const isCatalogueEnabled = Boolean(storeData?.configData?.storeConfig?.basicConfig?.catalogue);
        const isAllowedWithoutCatalogue = (
          current_page_url.includes('invoice') ||
          current_page_url.includes('membershipinvoice') ||
          current_page_url.includes('packageinvoice') ||
          current_page_url.includes('giftcardinvoice') ||
          current_page_url.includes('advanceinvoice') ||
          current_page_url.includes('advancerefund') ||
          current_page_url.includes('balanceinvoice') ||
          current_page_url.includes('feedback') ||
          current_page_url.includes('form') ||
          current_page_url.includes('postcare')
        );
        if (!isCatalogueEnabled && !isAllowedWithoutCatalogue) {
          router.replace('/404');
          return;
        }
        if (current_page_url.includes('-grp') || current_page_url.includes('home')) { // http://localhost:3000/dev-3/pune/home
          setcurrentPage('home');
        } else if (current_page_url.includes('-pdp')) { // http://localhost:3000/dev-3/pune/cat-m-srp/cat-m-pdp  product or service pdp page
          current_page_url = current_page_url.split('-pdp')[0];
          setcurrentPage('pdp');
          // current_page_url = router.query.pagepath[0]
        } else if (current_page_url.includes('-prp')) { // http://localhost:3000/dev-3/pune/cat-m-prp == product page url
          current_page_url = current_page_url.split('-prp')[0];
          setcurrentPage('all-Categories');
          setCurrentCategoryType(storeData?.keywords[PRODUCT]);
        } else if (current_page_url.includes('-srp')) { // http://localhost:3000/dev-3/pune/cat-m-srp == service page url
          current_page_url = current_page_url.split('-srp')[0];
          setCurrentCategoryType(storeData?.keywords[SERVICE]);
          setcurrentPage('all-Categories');
        } else if (current_page_url.includes('cart')) { // http://localhost:3000/dev-3/pune/cart == cart page url
          current_page_url = current_page_url.split('-srp')[0];
          setEnableDefaultLayout(false);
          setcurrentPage('cart');
        } else if (current_page_url.includes('checkout')) { // http://localhost:3000/dev-3/pune/cart/checkout == checkout page url
          setEnableDefaultLayout(false);
          setcurrentPage('checkout');
        } else if (current_page_url.includes('myorders')) { // http://localhost:3000/dev-3/pune/myorder == myorder page url
          setcurrentPage('myorders');
        } else if (router.asPath.includes('referral/add')) { // http://localhost:3000/dev-3/pune/referral/add == add referral page url
          setcurrentPage('referral-add');
        } else if (current_page_url.includes('referral')) { // http://localhost:3000/dev-3/pune/referral == referral page url
          setcurrentPage('referral');
        } else if (current_page_url.includes('appointmenthistory')) { // http://localhost:3000/dev-3/pune/myorder == myorder page url
          setcurrentPage('appointmenthistory');
        } else if (current_page_url.includes('profile')) { // http://localhost:3000/dev-3/pune/profile == profile page url
          setcurrentPage('profile');
        } else if (current_page_url.includes('orderconfirmation')) { // http://localhost:3000/dev-3/pune/orderconfirmation == orderconfirmation page url
          if (router.query.status !== 'paid') {
            dispatch(showError('Your payment has been failed.', 5000));
            let paymentFor = readLocalStorage("payment_type")
            if (paymentFor) {
              if (paymentFor.includes('order')) setcurrentPage('cart')
              if (paymentFor.includes('appointment')) setcurrentPage('appointment')
            } else {
              setcurrentPage('checkout');  // http://localhost:3000/dev-3/pune/checkout == if payment fail stay to checkout page
            }
          } else {
            setcurrentPage('orderconfirmation');  // http://localhost:3000/dev-3/pune/orderconfirmation == if payment paid redirect to orderConfirmation
          }
        } else if (current_page_url.includes('feedback')) { // http://localhost:3000/dev-3/pune/feedback?id=12345 == feedback page url
          setEnableDefaultLayout(false);
          setcurrentPage('feedback');
        } else if (current_page_url.includes('membershipinvoice')) { // http://localhost:3000/dev-3/pune/membershipinvoice?id=12345 == membershipinvoice page url
          setEnableDefaultLayout(false);
          setcurrentPage('membershipinvoice');
        } else if (current_page_url.includes('packageinvoice')) { // http://localhost:3000/dev-3/pune/packageinvoice?id=12345 == packageinvoice page url
          setEnableDefaultLayout(false);
          setcurrentPage('packageinvoice');
        } else if (current_page_url.includes('giftcardinvoice')) { // http://localhost:3000/dev-3/pune/membershipinvoice?id=12345 == membershipinvoice page url
          setEnableDefaultLayout(false);
          setcurrentPage('giftcardinvoice');
        } else if (current_page_url.includes('advanceinvoice')) { // http://localhost:3000/dev-3/pune/advancebalanceinvoice?id=12345 == packageinvoice page url
          setEnableDefaultLayout(false);
          setcurrentPage('advanceinvoice');
        } else if (current_page_url.includes('advancerefund')) { // http://localhost:3000/dev-3/pune/advancebalanceinvoice?id=12345 == packageinvoice page url
          setEnableDefaultLayout(false);
          setcurrentPage('advancerefund');
        } else if (current_page_url.includes('balanceinvoice')) { // http://localhost:3000/dev-3/pune/invoice?id=12345 == invoice page url
          setEnableDefaultLayout(false);
          setcurrentPage('balanceinvoice');
        } else if (current_page_url.includes('invoice')) { // http://localhost:3000/dev-3/pune/invoice?id=12345 == invoice page url
          setEnableDefaultLayout(false);
          setcurrentPage('invoice');
        } else if (current_page_url.includes('privacy')) { // http://localhost:3000/dev-3/pune/privacy == privacy page url
          setcurrentPage('privacy');
        } else if (current_page_url.includes('terms')) { // http://localhost:3000/dev-3/pune/terms == terms page url
          setcurrentPage('terms');
        } else if (current_page_url.includes('search')) { // http://localhost:3000/dev-3/pune/search == search page url
          setcurrentPage('search');
        } else if (current_page_url.includes('appointment')) { // http://localhost:3000/dev-3/pune/appointment == appointment page url
          setEnableDefaultLayout(false);
          setcurrentPage('appointment');
        } else if (current_page_url.includes('storelocator')) { // http://localhost:3000/dev-3/pune/storelocator == appointment page url
          setcurrentPage('storelocator');
        } else if (current_page_url.includes('form')) { // http://localhost:3000/dev-3/pune/forms == forms page url
          setEnableDefaultLayout(false);
          setcurrentPage('form');
        } else if (current_page_url.includes('payment')) {
          setcurrentPage('payment');
        } else if (current_page_url.includes('purchase')) { // http://localhost:3000/dev-3/pune/purchase?membership == membership view all page
          setEnableDefaultLayout(false);
          setcurrentPage('purchase');
        } else if (current_page_url.includes('postcare')) { // http://localhost:3000/dev-3/pune/postcare?serviceId=xxx
          setEnableDefaultLayout(false);
          setcurrentPage('postcare');
        } else {
          setcurrentPage('category'); // http://localhost:3000/dev-3/pune/home == home page url
        }
        current_page_url = current_page_url.split("-").join(" ");
        setUrl_Segment(current_page_url);
      } else { // http://localhost:3000/dev-3/pune/ 
        if (!Boolean(storeData?.configData?.storeConfig?.basicConfig?.catalogue)) {
          router.replace('/404');
          return;
        }
        setcurrentPage('home');
        setUrl_Segment('/');
      }
    }
  }, [router.asPath]);

  useEffect(() => {
    dispatch(updateCurrentPage(currentPage));
  }, [currentPage])

  useEffect(() => {
    if (windowRef()) {
      dispatch(syncLocalStorageAppointment());
      dispatch(syncLocalStorageOrder());
      writeLocalStorage(`decimalPlaces`, String(storeData.configData.decimalPlaces))
      writeLocalStorage(`currencySymbol`, String(storeData.configData.currencySymbol))
      writeLocalStorage(`enableRoundOff`, Boolean(storeData.configData.storeConfig?.basicConfig?.enableRoundOff) ? "1" : "0")

      let color = storeData.configData?.storeConfig?.sparkConfig?.color[activeGroup] || '#fea1c2';
      let textColor = storeData.configData?.storeConfig?.sparkConfig?.textColor ? storeData.configData?.storeConfig?.sparkConfig?.textColor[activeGroup] : '#282828';
      document.documentElement.style.setProperty('--primary-color', hex2rgb(color, ''), 'important');
      document.documentElement.style.setProperty('--primary-text-color', hex2rgb(textColor, ''), 'important');
      document.documentElement.style.setProperty('--primary-color-alpha', hex2rgb(color, 60), 'important');


      let resparkOverrideColor = storeData.configData?.storeConfig?.sparkConfig?.resparkOverrideColor;
      if (Boolean(resparkOverrideColor)) {
        document.documentElement.style.setProperty('--primary-respark-color', hex2rgb(resparkOverrideColor, ''), 'important');
        document.documentElement.style.setProperty('--primary-respark-color-alpha', hex2rgb(resparkOverrideColor, 12), 'important');
      }

      document.body.dataset.theme = activeGroup;
    }
  }, [windowRef, storeData])

  useEffect(() => {
    if (cookie['user']) {
      dispatch(updateUserData(cookie['user']));
    }
  }, [cookie])

  return <Default enableDefaultLayout={enableDefaultLayout} currentPage={currentPage}>

    {(storeData && storeData?.storeId) && url_Segment &&
      currentPage == 'home' ? <Home />
      :
      currentPage === 'category' ? <CategoryPage metaTags={metaTags} url_Segment={url_Segment} storeData={storeData} activeGroup={activeGroup} />
        :
        currentPage === 'pdp' ? <PdpPage url_Segment={url_Segment} metaTags={metaTags} storeData={storeData} />
          :
          currentPage === 'all-Categories' ? <AllCategoryPage metaTags={metaTags} url_Segment={url_Segment} storeData={storeData} activeGroup={activeGroup} type={currentCategoryType} />
            :
            currentPage == 'orderconfirmation' ? <OrderConfirmation inititalOrder={null} />
              :
              currentPage == 'privacy' ? <Privacy />
                :
                currentPage == 'myorders' ? <OrderHistoryPage />
                  :
                  currentPage == 'referral' ? <ReferralPage />
                    :
                    currentPage == 'referral-add' ? <AddReferralPage />
                      :
                      currentPage === 'appointmenthistory' ? <AppointmentHistoryPage />
                        :
                        currentPage == 'profile' ? <ProfilePage />
                          :
                          currentPage == 'terms' ? <Terms />
                            :
                            currentPage == 'storelocator' ? <StoreLocator storeData={storeData} />
                              :
                              currentPage == 'cart' ? <CartPage />
                                :
                                currentPage == 'checkout' ? <CheckoutPage />
                                  :
                                  currentPage == 'feedback' ? <FeedbackPage storeData={storeData} metaTags={metaTags} />
                                    :
                                    currentPage == 'appointment' ?
                                      <>
                                        <ServicePdpModal />
                                        {storeData?.configData.storeConfig?.appointmentConfig.showBookedSlot ? <BookedSlotAppointmentFlow /> : <Appointment />}
                                      </> :
                                      currentPage == 'purchase' ? <RedeemablePurchaseView />
                                        : currentPage == 'invoice' ? <InvoicePage />
                                          : currentPage == 'membershipinvoice' ? <MembershipInvoicePage />
                                            : currentPage == 'packageinvoice' ? <PackageInvoicePage />
                                              : currentPage == 'giftcardinvoice' ? <GiftCardInvoicePage />
                                                : (currentPage == 'balanceinvoice' || currentPage == 'advanceinvoice' || currentPage == 'advancerefund') ? <AdvanceBalanceInvoicePage />
                                                  : currentPage == 'form' ? <CustomForms initialFormDetails={null} />
                                                    : currentPage == 'payment' ? <PaymentPage />
                                                      : currentPage == 'postcare' ? <PostCarePage />
                                                        : null

    }
  </Default>
}

const isRetailItem = (item) => {
  if (item?.variations?.length) {
    const retailVariations = item.variations.filter((variation) => variation.isRetail === true);
    item.variations = retailVariations;
    return retailVariations.length > 0;
  }
  return item.isRetail === true;
};

const filterRetailCategories = (categories = []) => {
  return categories.map((category) => {
    if (category.type === "service") return category
    if (category?.itemList?.length) {
      category.itemList = category.itemList.filter(isRetailItem);
    }
    if (category?.categoryList?.length) {
      category.categoryList = category.categoryList.map((subCategory) => {
        if (subCategory?.itemList?.length) {
          subCategory.itemList = subCategory.itemList.filter(isRetailItem);
        }
        return subCategory;
      });
    }
    return category;
  });
};

export default PagePath;